import { CdkTrapFocus } from "@angular/cdk/a11y";
import { DOCUMENT } from "@angular/common";
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { MatMenuTrigger } from "@angular/material/menu";
import { Subscription } from "rxjs";
import { MemCacheService } from "src/app/services/mem-cache/mem-cache.service";

export interface SingleOption {
	label: string;
	value: string | number;
	icon?: string;
}

export interface DropdownSelectState {
	optionSelected: SingleOption;
}

@Component({
	selector: "app-single-select-dropdown",
	templateUrl: "./single-select-dropdown.component.html",
	styleUrls: ["./single-select-dropdown.component.scss"],
	encapsulation: ViewEncapsulation.None,
})
export class SingleSelectDropdownComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
	@Input() context: string;
	@Input() dropdownLabel: string;
	@Input() options: ReadonlyArray<SingleOption> = [];
	@Output() submitDropdownSelect = new EventEmitter<string | number>();

	@ViewChild(MatMenuTrigger) menuTrigger: MatMenuTrigger;
	@ViewChild(CdkTrapFocus) trapFocus: CdkTrapFocus;
	@ViewChild("dialogWrapper") dialogWrapperRef: ElementRef<HTMLElement>;

	showMenu = false;
	defaultState: DropdownSelectState = {
		optionSelected: null,
	};
	potentiallySelectedOption: SingleOption = null;
	selectedOption: SingleOption = null;
	state: DropdownSelectState;
	stateKey: string;
	subscriptions = new Subscription();
	displayOptions: Array<SingleOption>;

	constructor(public memCacheService: MemCacheService, @Inject(DOCUMENT) private readonly document: Document) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.options) {
			this.displayOptions = [...this.options];
		}
	}

	/**
	 * On Init
	 * Subscribe to mem cache service to get the state of the dropdown
	 */
	ngOnInit() {
		this.subscriptions.add(
			this.memCacheService.cleared$().subscribe(() => {
				this.memCacheService.setValue(this.stateKey, null);
				this.captureSelection();
				this.submitDropdownSelect.emit(null);
			}),
		);
	}

	/**
	 * After view Init
	 * Defer captureSelection until after the view detection cycle has completed
	 */
	ngAfterViewInit() {
		setTimeout(() => {
			this.captureSelection();
		});
	}

	/**
	 * On Destroy
	 * Unsubscribe from all subscriptions
	 */
	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	/**
	 * Initialize the component state
	 */
	initState() {
		this.stateKey = this.context;

		this.state = this.getState();

		if (!this.state) {
			this.state = {
				...this.defaultState,
			};
		}
	}

	handleOptionClick(option: SingleOption) {
		if (option.value === this.potentiallySelectedOption?.value) {
			this.potentiallySelectedOption = null;
		} else {
			this.potentiallySelectedOption = { ...option };
		}
	}

	dropdownSubmit() {
		if (!this.potentiallySelectedOption && !this.selectedOption) {
			this.menuTrigger.closeMenu();
			return;
		}

		this.selectedOption = this.potentiallySelectedOption ? { ...this.potentiallySelectedOption } : null;
		this.submitDropdownSelect.emit(this.selectedOption?.value || null);
		this.flushState();
		this.menuTrigger.closeMenu();
	}

	handleMenuOpen() {
		this.showMenu = true;
		this.applyMenuPanelAria();

		if (this.selectedOption) {
			this.potentiallySelectedOption = { ...this.selectedOption };
		} else {
			this.potentiallySelectedOption = null;
		}

		setTimeout(() => this.trapFocus?.focusTrap?.focusFirstTabbableElement());
	}

	private applyMenuPanelAria() {
		setTimeout(() => {
			const panelId = this.menuTrigger?.menu?.panelId;
			const panel = panelId ? (this.document.getElementById(panelId) as HTMLElement | null) : null;
			if (!panel) return;
			panel.setAttribute("role", "presentation");
			panel.removeAttribute("aria-label");
		});
	}

	handleMenuClose() {
		this.showMenu = false;
	}

	handleMenuKeydown(event: KeyboardEvent): void {
		if (["ArrowDown", "ArrowUp", "Home", "End", "PageDown", "PageUp"].includes(event.key)) {
			const activeEl = this.getDeepActiveElement();
			const wrapper = this.dialogWrapperRef?.nativeElement;
			const container = wrapper?.querySelector(".dropdown-option-container");

			// Only intercept these keys if focus is within the listbox
			if (!container?.contains(activeEl)) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();

			const optionBtns = Array.from(
				wrapper.querySelectorAll<HTMLElement>(".dropdown-option-container .option:not([disabled])"),
			);
			if (!optionBtns.length) return;

			const currentIndex = optionBtns.indexOf(activeEl as HTMLElement);

			if (event.key === "ArrowDown") {
				const next = currentIndex >= optionBtns.length - 1 ? 0 : currentIndex + 1;
				optionBtns[next].focus();
			} else if (event.key === "ArrowUp") {
				const prev = currentIndex <= 0 ? optionBtns.length - 1 : currentIndex - 1;
				optionBtns[prev].focus();
			} else if (event.key === "Home") {
				optionBtns[0].focus();
			} else if (event.key === "End") {
				optionBtns[optionBtns.length - 1].focus();
			} else if (event.key === "PageDown") {
				const next = Math.min(currentIndex + 6, optionBtns.length - 1);
				optionBtns[next].focus();
			} else if (event.key === "PageUp") {
				const prev = Math.max(currentIndex - 6, 0);
				optionBtns[prev].focus();
			}
			return;
		}

		if (event.key !== "Tab") return;

		event.preventDefault();
		event.stopPropagation();

		const wrapper = this.dialogWrapperRef?.nativeElement;
		if (!wrapper) return;

		const focusable: HTMLElement[] = [];
		this.collectFocusableElements(wrapper, focusable);

		if (!focusable.length) return;

		let activeEl = this.getDeepActiveElement() as HTMLElement;
		const container = wrapper.querySelector(".dropdown-option-container");
		if (container?.contains(activeEl)) {
			activeEl = container as HTMLElement;
		}

		const currentIndex = focusable.indexOf(activeEl);

		if (event.shiftKey) {
			const prev = currentIndex <= 0 ? focusable.length - 1 : currentIndex - 1;
			focusable[prev].focus();
		} else {
			const next = currentIndex >= focusable.length - 1 ? 0 : currentIndex + 1;
			focusable[next].focus();
		}
	}

	focusFirstOption(): void {
		const wrapper = this.dialogWrapperRef?.nativeElement;
		if (!wrapper) return;

		// Focus the selected option if there is one
		const selectedOption = wrapper.querySelector<HTMLElement>(
			".dropdown-option-container .option-wrapper-selected .option:not([disabled])",
		);
		if (selectedOption) {
			selectedOption.focus();
			return;
		}

		// Fallback to the very first option
		const optionBtns = Array.from(
			wrapper.querySelectorAll<HTMLElement>(".dropdown-option-container .option:not([disabled])"),
		);
		if (optionBtns.length) {
			optionBtns[0].focus();
		}
	}

	private collectFocusableElements(node: Element | ShadowRoot, result: HTMLElement[]): void {
		for (const child of Array.from(node.children)) {
			if (this.isFocusableElement(child as HTMLElement)) {
				result.push(child as HTMLElement);
			}
			if ((child as Element).shadowRoot) {
				this.collectFocusableElements((child as Element).shadowRoot, result);
			} else {
				this.collectFocusableElements(child as Element, result);
			}
		}
	}

	private isFocusableElement(el: HTMLElement): boolean {
		if ((el as HTMLButtonElement).disabled) return false;
		const tabindex = el.getAttribute("tabindex");
		if (tabindex === "-1") return false;
		if (el.classList.contains("cdk-focus-trap-anchor")) return false;
		const tag = el.tagName.toLowerCase();
		if (["button", "input", "select", "textarea"].includes(tag)) return true;
		if (tag === "a" && el.hasAttribute("href")) return true;
		if (tabindex !== null) return true;
		return false;
	}

	private getDeepActiveElement(): Element | null {
		let el: Element | null = document.activeElement;
		while (el?.shadowRoot?.activeElement) {
			el = el.shadowRoot.activeElement;
		}
		return el;
	}

	/**
	 * Update cached dropdown state
	 */
	flushState() {
		this.memCacheService.setValue<DropdownSelectState>(this.stateKey, {
			optionSelected: { ...this.selectedOption },
		});
	}

	/**
	 * Get current dropdown single select state from cache
	 * @returns DropdownSingleSelectState object
	 */
	private getState() {
		return this.memCacheService.getValue<DropdownSelectState>(this.stateKey);
	}

	/**
	 * Capture current dropdown select state
	 */
	private captureSelection() {
		this.initState();

		if (this.state.optionSelected) {
			this.potentiallySelectedOption = { ...this.state.optionSelected };
			this.selectedOption = { ...this.state.optionSelected };
		} else {
			this.potentiallySelectedOption = null;
			this.selectedOption = null;
		}
	}

	isInspectionType() {
		return this.context && this.context.includes("inspectionType");
	}
}
